
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import CourseSearchInline from '@/components/courses/CourseSearchInline';
import AddCourseDialog from '@/components/courses/AddCourseDialog';
import HowToPlayContent from '@/components/HowToPlayContent';
import { setHomeCourse, addCourseToUser } from '@/integrations/supabase/profileCourseService';
import { getFriendCourses } from '@/integrations/supabase/courseService';
import { supabase } from '@/integrations/supabase/client';
import { useCourses } from '@/hooks/useCourses';
import { toast } from '@/hooks/use-toast';
import { Loader2, MapPin, Users } from 'lucide-react';

const COMPLETED_KEY = 'onboarding_v2_completed';

interface SimpleCourse {
  id: string;
  name: string;
  city: string;
  state: string;
}

const TOTAL_STEPS = 4;

const StepDots = ({ current }: { current: number }) => (
  <div className="flex items-center justify-center gap-2 py-4">
    {Array.from({ length: TOTAL_STEPS }, (_, i) => (
      <div
        key={i}
        className={`h-2 w-2 rounded-full transition-colors ${
          i + 1 === current ? 'bg-forest' : 'bg-border'
        }`}
      />
    ))}
  </div>
);

const Onboarding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { userCourses, refreshUserCourses } = useCourses();

  const [step, setStep] = useState(1);
  const [friendCourses, setFriendCourses] = useState<SimpleCourse[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasHomeCourse, setHasHomeCourse] = useState(false);

  // Check if user already has a home course + fetch friend courses
  useEffect(() => {
    if (!user?.id) return;
    getFriendCourses(user.id).then(setFriendCourses);
    supabase
      .from('profiles')
      .select('home_course_id')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.home_course_id) setHasHomeCourse(true);
      });
  }, [user?.id]);

  const setAsHomeCourse = async (courseId: string, courseName: string) => {
    if (!user || isSaving) return;

    setIsSaving(true);
    try {
      await addCourseToUser(user.id, courseId, true);

      const result = await setHomeCourse(user.id, courseId);
      if (result.error) throw result.error;

      toast({
        title: 'Home course set!',
        description: `${courseName} is now your home course.`,
      });

      localStorage.setItem(COMPLETED_KEY, '1');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error setting home course:', error);
      toast({
        title: 'Error',
        description: 'Failed to set your home course. Please try again.',
        variant: 'destructive',
      });
      setIsSaving(false);
    }
  };

  const completeOnboarding = () => {
    localStorage.setItem(COMPLETED_KEY, '1');
    window.location.href = '/';
  };

  // Build user courses as SimpleCourse (dedup against friend courses)
  const friendCourseIds = new Set(friendCourses.map(c => c.id));
  const userCoursesSimple: SimpleCourse[] = userCourses
    .filter(c => !friendCourseIds.has(c.id))
    .map(c => ({
      id: c.id,
      name: c.name,
      city: c.city || '',
      state: c.state || '',
    }));

  return (
    <div className="min-h-screen pb-24 bg-cream">
      {/* Saving overlay */}
      {isSaving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3 shadow-lg">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
            <p className="text-sm font-medium">Setting home course...</p>
          </div>
        </div>
      )}

      <StepDots current={step} />

      {/* Step 1: Welcome */}
      {step === 1 && (
        <div className="px-4 pt-4 pb-2 text-center max-w-md mx-auto">
          <div className="w-16 h-16 bg-forest rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⛳</span>
          </div>
          <h1 className="font-display text-2xl font-semibold text-forest mb-3">
            What is the Birdie Game?
          </h1>
          <p className="text-muted-foreground leading-relaxed mb-8">
            A season-long challenge played on the course with your friends.
            Every time you make a birdie (or better), log it here. The player
            who birdies the most unique holes wins. Play solo with Lone Wolf or
            compete in a group.
          </p>
          <button
            onClick={() => setStep(2)}
            className="w-full max-w-xs mx-auto bg-forest text-white font-medium py-3 rounded-lg hover:bg-forest/90 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      {/* Step 2: Rules */}
      {step === 2 && (
        <div className="px-4 pt-4 pb-2 max-w-2xl mx-auto">
          <h1 className="font-display text-2xl font-semibold text-forest mb-1 text-center">
            How to Play
          </h1>
          <p className="text-sm text-muted-foreground mb-6 text-center">Three simple rules</p>
          <HowToPlayContent hideLoneWolf />
          <div className="mt-8 text-center">
            <button
              onClick={() => setStep(3)}
              className="w-full max-w-xs mx-auto bg-forest text-white font-medium py-3 rounded-lg hover:bg-forest/90 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Lone Wolf */}
      {step === 3 && (
        <div className="px-4 pt-4 pb-2 text-center max-w-md mx-auto">
          <div className="rounded-xl border border-gold/30 bg-gold/5 p-5 shadow-sm text-left mb-8">
            <h2 className="font-display text-lg font-semibold text-forest mb-1">
              Lone Wolf
            </h2>
            <p className="text-xs font-medium text-gold mb-2">Solo Mode</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Birdie all 18 holes on your own. Your birdies from group games
              count toward your solo progress too.
            </p>
          </div>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Your Lone Wolf game starts automatically when you pick a home course.
          </p>
          <button
            onClick={() => hasHomeCourse ? completeOnboarding() : setStep(4)}
            className="w-full max-w-xs mx-auto bg-forest text-white font-medium py-3 rounded-lg hover:bg-forest/90 transition-colors"
          >
            {hasHomeCourse ? "Let's Go" : 'Next'}
          </button>
        </div>
      )}

      {/* Step 4: Home Course */}
      {step === 4 && (
        <div className="px-4 pt-4 pb-2 max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-forest rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⛳</span>
            </div>
            <h1 className="font-display text-2xl font-semibold text-forest">
              Pick Your Home Course
            </h1>
            <p className="text-muted-foreground mt-1">
              Choose your home course to start your Lone Wolf game.
            </p>
          </div>

          {/* Quick picks: friends' courses + user's courses */}
          {(friendCourses.length > 0 || userCoursesSimple.length > 0) && (
            <div className="space-y-2.5 mb-6">
              {friendCourses.map(course => (
                <button
                  key={course.id}
                  onClick={() => setAsHomeCourse(course.id, course.name)}
                  disabled={isSaving}
                  className="w-full text-left px-4 py-3.5 rounded-xl border border-border/50 bg-white shadow-sm hover:border-forest/30 hover:shadow-md transition-all disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-forest">{course.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {course.city}{course.state ? `, ${course.state}` : ''}
                      </p>
                    </div>
                    <Users className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                  </div>
                </button>
              ))}
              {userCoursesSimple.map(course => (
                <button
                  key={course.id}
                  onClick={() => setAsHomeCourse(course.id, course.name)}
                  disabled={isSaving}
                  className="w-full text-left px-4 py-3.5 rounded-xl border border-border/50 bg-white shadow-sm hover:border-forest/30 hover:shadow-md transition-all disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm text-forest">{course.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {course.city}{course.state ? `, ${course.state}` : ''}
                      </p>
                    </div>
                    <MapPin className="h-4 w-4 text-muted-foreground/50 shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Divider */}
          {(friendCourses.length > 0 || userCoursesSimple.length > 0) && (
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">or search</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          {/* Search */}
          <div className="mb-3">
            <CourseSearchInline
              onImported={refreshUserCourses}
              onSelectCourse={(course) => setAsHomeCourse(course.id, course.name)}
            />
          </div>

          <AddCourseDialog
            onAddCourse={refreshUserCourses}
            onCourseCreated={(info) => setAsHomeCourse(info.id, info.name)}
            trigger={
              <button className="text-sm text-muted-foreground hover:text-forest transition-colors">
                Add a course manually
              </button>
            }
          />

          {/* Skip */}
          <div className="mt-10 text-center">
            <button
              onClick={completeOnboarding}
              className="text-sm text-muted-foreground/70 hover:text-muted-foreground transition-colors"
            >
              Do this later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
